/**
 * Upload speed and time-left estimation (build 129).
 *
 * Pure module (no React, no path aliases) so it can be unit tested with
 * `node --test` (see tests/js/upload-eta.test.ts).
 */

export interface SpeedSample {
  t: number;
  bytes: number;
}

export interface SpeedEstimatorOptions {
  // Sliding window of recent progress events used for the raw rate.
  windowMs?: number;
  // Show "Estimating…" at least this long after a (re)start.
  warmupMs?: number;
  // …and until this many progress events arrived.
  minSamples?: number;
  // Time constant of the exponential smoothing.
  tauMs?: number;
  // No progress for this long: the speed decays towards the real rate.
  stallMs?: number;
}

/**
 * Exponentially smoothed moving average of the rate measured over a sliding
 * window. The first sample after a (re)start is only a baseline, so bytes that
 * were already on the server (resume) never count as speed.
 */
export class SpeedEstimator {
  private samples: SpeedSample[] = [];
  private smoothed: number | null = null;
  private startedAt: number | null = null;
  private lastEmaAt: number | null = null;
  private readonly windowMs: number;
  private readonly warmupMs: number;
  private readonly minSamples: number;
  private readonly tauMs: number;
  private readonly stallMs: number;

  constructor(options: SpeedEstimatorOptions = {}) {
    this.windowMs = options.windowMs ?? 10_000;
    this.warmupMs = options.warmupMs ?? 3_000;
    this.minSamples = options.minSamples ?? 3;
    this.tauMs = options.tauMs ?? 4_000;
    this.stallMs = options.stallMs ?? 2_500;
  }

  /** Forget everything: after pause, resume, offline or a restart. */
  reset(): void {
    this.samples = [];
    this.smoothed = null;
    this.startedAt = null;
    this.lastEmaAt = null;
  }

  get sampleCount(): number {
    return this.samples.length;
  }

  addSample(bytes: number, now: number): void {
    if (this.startedAt === null) {
      // Baseline only (may include bytes resumed from the server).
      this.startedAt = now;
      this.samples = [{t: now, bytes}];
      return;
    }
    const last = this.samples[this.samples.length - 1];
    if (bytes < last.bytes) {
      // Progress went backwards (a failed part is re-sent): start over.
      this.reset();
      this.addSample(bytes, now);
      return;
    }
    if (now <= last.t) {
      last.bytes = bytes;
      return;
    }
    this.samples.push({t: now, bytes});
    while (this.samples.length > 2 && this.samples[1].t <= now - this.windowMs) {
      this.samples.shift();
    }
    const first = this.samples[0];
    const seconds = (now - first.t) / 1000;
    if (seconds <= 0) return;
    const rate = (bytes - first.bytes) / seconds;
    if (this.smoothed === null || this.lastEmaAt === null) {
      this.smoothed = rate;
    } else {
      const alpha = 1 - Math.exp(-(now - this.lastEmaAt) / this.tauMs);
      this.smoothed += alpha * (rate - this.smoothed);
    }
    this.lastEmaAt = now;
  }

  isEstimating(now: number): boolean {
    return (
      this.startedAt === null ||
      now - this.startedAt < this.warmupMs ||
      this.samples.length < this.minSamples ||
      this.smoothed === null ||
      this.smoothed <= 0
    );
  }

  /** Bytes per second, or null while estimating. */
  speed(now: number): number | null {
    if (this.isEstimating(now) || this.smoothed === null) return null;
    const last = this.samples[this.samples.length - 1];
    if (now - last.t > this.stallMs) {
      // Nothing arrived for a while: don't keep showing a stale high speed.
      const first = this.samples[0];
      const seconds = (now - first.t) / 1000;
      const real = seconds > 0 ? (last.bytes - first.bytes) / seconds : 0;
      return Math.max(0, Math.min(this.smoothed, real));
    }
    return this.smoothed;
  }

  /** Seconds left for `remainingBytes`, or null while estimating/stalled. */
  eta(remainingBytes: number, now: number): number | null {
    if (remainingBytes <= 0) return 0;
    const speed = this.speed(now);
    if (!speed || speed <= 0) return null;
    return remainingBytes / speed;
  }
}

export interface EtaFileState {
  remaining: number;
  // Smoothed speed of a running file (null while it is estimating).
  speed: number | null;
  active: boolean;
  // Waiting in the queue: its bytes still have to go over the same link.
  queued: boolean;
}

export interface OverallEta {
  seconds: number | null;
  speed: number | null;
}

/**
 * Time left for everything: remaining bytes of running + queued files divided
 * by the combined speed of the running files. Queued files start when others
 * finish, so the combined throughput stays roughly the same; a running file
 * that is still estimating is assumed to go as fast as the average of the
 * others.
 */
export function overallEta(files: EtaFileState[]): OverallEta {
  let remaining = 0;
  let speed = 0;
  let measured = 0;
  let active = 0;
  for (const file of files) {
    if (file.active) {
      active++;
      remaining += Math.max(0, file.remaining);
      if (file.speed && file.speed > 0) {
        speed += file.speed;
        measured++;
      }
    } else if (file.queued) {
      remaining += Math.max(0, file.remaining);
    }
  }
  if (!active) return {seconds: null, speed: null};
  if (!measured) return {seconds: null, speed: null};
  const combined = (speed / measured) * active;
  return {seconds: remaining / combined, speed: combined};
}

export interface EtaMessage {
  message: string;
  values?: Record<string, number>;
}

/** Row / header wording: "Less than a minute left", "About 3 min left", "1 h 12 min left". */
export function timeLeftMessage(seconds: number): EtaMessage {
  if (seconds < 45) return {message: 'Less than a minute left'};
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) {
    return {
      message: '[one About 1 min left|other About :count min left]',
      values: {count: minutes},
    };
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!rest) {
    return {
      message: '[one About 1 h left|other About :count h left]',
      values: {count: hours},
    };
  }
  return {
    message: ':hours h :minutes min left',
    values: {hours, minutes: rest},
  };
}

/** Compact wording for the pill and "Done · …": "< 1 min", "5 min", "1 h 12 min", "42 s". */
export function shortDurationMessage(
  seconds: number,
  withSeconds = false,
): EtaMessage {
  if (withSeconds && seconds < 60) {
    return {message: ':count s', values: {count: Math.max(1, Math.round(seconds))}};
  }
  if (seconds < 45) return {message: '< 1 min'};
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return {message: ':count min', values: {count: minutes}};
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!rest) return {message: ':count h', values: {count: hours}};
  return {message: ':hours h :minutes min', values: {hours, minutes: rest}};
}

const SPEED_UNITS = ['B/s', 'KB/s', 'MB/s', 'GB/s', 'TB/s'];
const formatters = new Map<string, Intl.NumberFormat>();

/**
 * "12.4 MB/s" (1024-based like the byte sizes in the app). Latin digits in
 * every language, matching the byte sizes shown next to it.
 */
export function formatSpeed(bytesPerSecond: number, locale = 'en'): string {
  let value = Math.max(0, bytesPerSecond) / 1024;
  let unit = 1;
  while (value >= 1000 && unit < SPEED_UNITS.length - 1) {
    value /= 1024;
    unit++;
  }
  const digits = value < 10 ? 1 : 0;
  const key = `${locale}|${digits}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    let tag = locale;
    try {
      tag = Intl.getCanonicalLocales(locale)[0] ?? 'en';
    } catch {
      tag = 'en';
    }
    formatter = new Intl.NumberFormat(`${tag}-u-nu-latn`, {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
      useGrouping: false,
    });
    formatters.set(key, formatter);
  }
  return `${formatter.format(value)} ${SPEED_UNITS[unit]}`;
}

export interface CountdownState {
  seconds: number;
  at: number;
}

/**
 * Display smoothing for a time-left value: the previous value counts down in
 * real time and only moves part of the way towards a new raw estimate, so the
 * number does not jump back and forth between ticks.
 */
export function smoothCountdown(
  previous: CountdownState | null,
  raw: number | null,
  now: number,
  weight = 0.3,
): CountdownState | null {
  if (raw == null) return null;
  if (!previous || now - previous.at > 10_000) return {seconds: raw, at: now};
  const predicted = Math.max(0, previous.seconds - (now - previous.at) / 1000);
  // Big changes (a file added or removed) are taken over at once.
  if (Math.abs(raw - predicted) > Math.max(60, predicted)) {
    return {seconds: raw, at: now};
  }
  return {seconds: predicted + weight * (raw - predicted), at: now};
}
